import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatDateTime } from "../../globalFunctions/DateTimeDisplayCleaner";
import { XMarkIcon } from "@heroicons/react/24/solid";
import QrScanner from "../../components/controls/QrScanner";
import DropdownMenu from "../../components/controls/DropdownMenu";
import FormInput from "../../components/controls/FormInput";
import Button from "../../components/controls/Button";

export default function TicketCheckinPage() {
  const { t } = useTranslation();
  const [code, setCode] = useState("");
  const [selectedEventId, setSelectedEventId] = useState("");
  const [events, setEvents] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchEmail, setSearchEmail] = useState("");
  const [emailTickets, setEmailTickets] = useState([]);
  const [emailError, setEmailError] = useState("");
  const [scannerVisible, setScannerVisible] = useState(false);
  const [scannerPermissionDenied, setScannerPermissionDenied] = useState(false);

  useEffect(() => {
    const fetchEvents = async () => {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/Event?showAll=false`
      );
      const data = await response.json();
      data.forEach((event) => (event.name = event.name.toUpperCase()));
      setEvents(Array.isArray(data) ? data : []);
    };
    fetchEvents();
  }, []);

  const eventItems = useMemo(() => {
    const base = [
      {
        key: "__none",
        value: "",
        label: `-- ${t("admin.chooseEvent") || "Choisir un événement"} --`,
      },
    ];

    const mapped = (events || []).map((e) => ({
      key: e.id,
      value: e.id,
      label: `${e.name} (${formatDateTime(e.eventDate)})`,
    }));

    return [...base, ...mapped];
  }, [events, t]);

  const selectedEventLabel = useMemo(() => {
    if (!selectedEventId)
      return `-- ${t("admin.chooseEvent") || "Choisir un événement"} --`;
    const found = (events || []).find((e) => e.id === selectedEventId);
    if (!found)
      return `-- ${t("admin.chooseEvent") || "Choisir un événement"} --`;
    return `${found.name} (${formatDateTime(found.eventDate)})`;
  }, [events, selectedEventId, t]);

  const resetSearchState = () => {
    setResult(null);
    setError("");
    setEmailTickets([]);
    setEmailError("");
  };

  const formatTicketCode = (value) =>
    value?.startsWith("AR3-") ? value : `AR3-${value}`;

  const handleCheck = async () => {
    if (!code || !selectedEventId) return;

    setLoading(true);
    resetSearchState();
    setSearchEmail("");

    const formattedCode = formatTicketCode(code);

    try {
      const response = await fetch(
        `${
          process.env.REACT_APP_API_BASE_URL
        }/api/Event/ticket/validate?code=${encodeURIComponent(formattedCode)}`
      );

      if (!response.ok) {
        setError("Billet non valide ou non payé.");
        return;
      }

      const data = await response.json();

      if (data.eventId !== selectedEventId) {
        setError("Ce billet n'appartient pas à l'événement sélectionné.");
        return;
      }

      if (data.isCheckedIn) {
        setError("Ce billet a déjà été validé.");
        return;
      }

      setResult(data);
    } catch {
      setError("Erreur lors de la validation.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearchByEmail = async () => {
    if (!searchEmail || !selectedEventId) return;

    setEmailTickets([]);
    setEmailError("");
    setError("");
    setCode("");
    setResult(null);

    try {
      const response = await fetch(
        `${
          process.env.REACT_APP_API_BASE_URL
        }/api/Event/tickets/by-email?email=${encodeURIComponent(searchEmail)}`
      );

      if (!response.ok) {
        setEmailError("Aucun billet trouvé pour cet email.");
        return;
      }

      const data = await response.json();
      setEmailTickets(Array.isArray(data) ? data : []);
    } catch {
      setEmailError("Erreur lors de la recherche.");
    }
  };

  const handleValidateTicket = async (ticketId) => {
    const response = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/Event/ticket/validate/checkin`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ticketId }),
      }
    );

    if (response.ok) {
      setEmailTickets((prev) =>
        prev.map((t) => (t.id === ticketId ? { ...t, isCheckedIn: true } : t))
      );
      setResult((prev) =>
        prev?.ticketId === ticketId ? { ...prev, isCheckedIn: true } : prev
      );
    }
  };

  const handleCheckWithCode = async (scanCode) => {
    if (!selectedEventId) return;

    setLoading(true);
    setResult(null);
    setError("");
    setEmailTickets([]);
    setEmailError("");
    setSearchEmail("");

    try {
      const response = await fetch(
        `${
          process.env.REACT_APP_API_BASE_URL
        }/api/Event/ticket/validate?code=${encodeURIComponent(scanCode)}`
      );

      if (!response.ok) {
        setError("Billet non valide ou non payé.");
        return;
      }

      const data = await response.json();

      if (data.eventId !== selectedEventId) {
        setError("Ce billet n'appartient pas à l'événement sélectionné.");
        return;
      }

      if (data.isCheckedIn) {
        setError("Ce billet a déjà été validé.");
        return;
      }

      setResult(data);
    } catch {
      setError("Erreur lors de la validation.");
    } finally {
      setLoading(false);
    }
  };

  const handleScan = (data) => {
    if (!data) return;
    const formattedCode = formatTicketCode(data);
    if (formattedCode === code) return;
    setCode(formattedCode);
    handleCheckWithCode(formattedCode);
  };

  const handleScannerError = (err) => {
    const msg = String(err?.message || err || "").toLowerCase();
    const denied =
      msg.includes("permission") ||
      msg.includes("denied") ||
      msg.includes("notallowederror") ||
      msg.includes("not allowed") ||
      msg.includes("securityerror");

    if (denied) {
      setScannerPermissionDenied(true);
      setScannerVisible(false);
      return;
    }

    setError("Erreur lors de l'ouverture du scanner.");
    setScannerVisible(false);
  };

  const openScanner = () => {
    setScannerPermissionDenied(false);
    setScannerVisible(true);
  };

  return (
    <div className="max-w-xl mx-auto p-6 bg-white rounded-xl shadow-md mt-10 text-primary">
      <h1 className="text-2xl font-bold mb-4">{"Validation des billets"}</h1>

      <div className="mb-4">
        <DropdownMenu
          title={selectedEventLabel}
          isDropdown
          withSearch
          searchPlaceholder={t("admin.chooseEvent") || "Choisir un événement"}
          items={eventItems.map((it) => ({
            ...it,
            onClick: () => setSelectedEventId(it.value),
          }))}
        />
      </div>

      <div className="mb-4 flex flex-col gap-2">
        <Button
          type="button"
          color="secondary"
          size="sm"
          onClick={() => {
            if (scannerVisible) {
              setScannerVisible(false);
              setScannerPermissionDenied(false);
              return;
            }
            openScanner();
          }}
          disabled={!selectedEventId}
        >
          {scannerVisible ? "Cacher le scanner" : "Afficher le scanner"}
        </Button>

        {scannerPermissionDenied && (
          <div className="p-3 border rounded-lg bg-gray-50 text-sm">
            <div className="text-red-600 font-semibold">
              impossible d'ouvrir le scanner car vous n'avez pas donné accès a
              la caméra
            </div>
            <div className="mt-2">
              <Button
                type="button"
                color="primary"
                size="sm"
                onClick={openScanner}
                disabled={!selectedEventId}
              >
                Réessayer
              </Button>
            </div>
          </div>
        )}
      </div>

      {scannerVisible && (
        <div className="mb-6">
          <h2 className="block font-semibold mb-2">Scanner un billet QR</h2>
          <QrScanner onScan={handleScan} onError={handleScannerError} />
        </div>
      )}

      <h2 className="block font-semibold mb-2">Recherche par # billet</h2>
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="flex-1">
          <FormInput
            label=""
            value={code}
            placeholder="Entrer le code du billet"
            onChange={(name, value) => setCode(value)}
            disabled={loading || !selectedEventId}
          />
        </div>
        <div className="sm:w-[170px] flex items-end">
          <Button
            type="button"
            color="primary"
            size="sm"
            onClick={handleCheck}
            disabled={loading || !selectedEventId}
            className="w-full"
          >
            {loading ? t("loading") : "Rechercher"}
          </Button>
        </div>
      </div>

      <h2 className="block font-semibold mb-2">
        Recherche par adresse courriel
      </h2>
      <div className="flex flex-col sm:flex-row gap-2 mb-2">
        <div className="flex-1">
          <FormInput
            label=""
            value={searchEmail}
            placeholder="Entrer l'adresse courriel"
            onChange={(name, value) => setSearchEmail(value)}
            disabled={loading || !selectedEventId}
          />
        </div>
        <div className="sm:w-[170px] flex items-end">
          <Button
            type="button"
            color="secondary"
            size="sm"
            onClick={handleSearchByEmail}
            disabled={loading || !selectedEventId}
            className="w-full"
          >
            Rechercher
          </Button>
        </div>
      </div>

      {result && (
        <div className="mt-4 p-4 border rounded bg-gray-50 space-y-2">
          <p className="text-green-600 font-semibold">
            ✅ Billet valide et payé
          </p>
          <div className="text-sm text-gray-700 space-y-1">
            <p>
              <strong>Événement :</strong> {result.eventName}
            </p>
            <p>
              <strong>Date :</strong> {formatDateTime(result.eventDate)}
            </p>
            <p>
              <strong>Acheteur :</strong> {result.buyerEmail}
            </p>
            <p>
              <strong>Acheté le :</strong> {formatDateTime(result.purchaseDate)}
            </p>

            {!result.isCheckedIn && (
              <Button
                type="button"
                color="accent"
                size="sm"
                onClick={() =>
                  handleValidateTicket(result.ticketId || result.id)
                }
              >
                Marquer Présent
              </Button>
            )}
          </div>
        </div>
      )}

      {error && (
        <p className="flex items-center gap-2 text-red-500 font-bold mt-4">
          <XMarkIcon className="w-8 h-8" />
          {error}
        </p>
      )}

      {emailError && <p className="text-red-500 text-sm mt-4">{emailError}</p>}

      {emailTickets.length > 0 && (
        <div className="space-y-3 mt-6">
          {emailTickets.map((ticket) => (
            <div
              key={ticket.id}
              className="p-3 border rounded bg-gray-50 flex flex-col md:flex-row md:items-center md:justify-between gap-2"
            >
              <div>
                <p className="text-sm">
                  <strong>Événement :</strong> {ticket.eventName} -{" "}
                  {formatDateTime(ticket.eventDate)}
                </p>
                <p className="text-sm">
                  <strong>Code :</strong>{" "}
                  <span className="font-mono">{ticket.code}</span>
                </p>
                <p className="text-sm">
                  <strong>Présence :</strong>{" "}
                  {ticket.isCheckedIn ? (
                    <span className="text-green-600">Déjà validé</span>
                  ) : (
                    <span className="text-yellow-600">Non validé</span>
                  )}
                </p>
              </div>

              {!ticket.isCheckedIn && (
                <Button
                  type="button"
                  color="accent"
                  size="sm"
                  onClick={() => handleValidateTicket(ticket.id)}
                >
                  Marquer Présent
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
